import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
} from '@headlessui/react';
import PersonIcon from '@/shared/icons/PersonIcon';
import useTeamManagementStore from '@/stores/teamManagement/teamManagementStore';
import { useUserStore } from '@/stores/user/userStore';
import { useChatbotStore } from '@/stores/chatbot/chatbotStore';
import * as teamManagementService from '@/services/teamManagement.service';
import * as userService from '@/services/user.service';
import featureCheck, { checkCanAddTeamMember } from '@/helpers/tier/featureCheck';
import ChevronDownIcon from '@/shared/icons/ChevronDownIcon';
import IconWrapper from '@/shared/components/UI/IconWrapper';
import UsersIcon from '@/shared/icons/UsersIcon';
import TriangleDangerIcon from '@/shared/icons/TriangleDangerIcon';
import AddUserIcon from '@/shared/icons/AddUserIcon';
import { formatCredits } from '@/utils/formatCredits';
import AddMember from '@/pages/TeamManagement/AddMember';
import { useTimezoneStore } from '@/stores/timezone/timezoneStore';
import useToast from '@/hooks/useToast';
import useModalStore from '@/stores/modal/modalStore';
import DSpinner from '@/components/Global/DSpinner';
import mixpanel from '@/utils/mixpanel';
import { toTierGroup } from '@/helpers/tier/constants';

import RightChevronIcon from '@/shared/icons/RightChevronIcon';
import LeftArrowIcon from '@/shared/icons/LeftArrowIcon';

const TeamManagementDropdown = ({ isMobileView = false, isMobileButton = false, onMobileButtonClick, onBack, onClose }) => {
  const {
    userMode,
    selectedTeam,
    setSelectedTeam,
    setUserMode,
    setTeamName,
    teamName,
    setTeams,
    setOwner,
    teams,
  } = useTeamManagementStore((state) => state);
  const userData = useUserStore((state) => state.user);
  const setUser = useUserStore((state) => state.setUser);
  const resetSelectedChatbot = useChatbotStore((state) => state.resetSelectedChatbot);
  const navigate = useNavigate();
  const [teamDropdownOpened, setTeamDropdownOpened] = useState(false);
  const [openAddMember, setOpenAddMember] = useState(false);
  const [selectedTeamForInvite, setSelectedTeamForInvite] = useState(null);
  const [roles, setRoles] = useState([]);
  const [teamData, setTeamData] = useState([]);
  const [currentUserRole, setCurrentUserRole] = useState(null);
  const [isLoadingRole, setIsLoadingRole] = useState(false);
  const [newMember, setNewMember] = useState({
    email: '',
    team_role_id: '',
    all_knowledge_bases: false,
    allowed_kbs: [],
    credits_available: 100,
  });
  const [error, setError] = useState({});
  const [loading, setLoading] = useState(false);
  const [inviteButtonLoading, setInviteButtonLoading] = useState(false);
  const timezone = useTimezoneStore((state) => state.timezone);
  const { addSuccessToast, addErrorToast } = useToast();
  const openPlansModal = useModalStore((state) => state.openPlansModal);

  // Handle tier label click to open plans modal
  const handleTierLabelClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    openPlansModal();
  };

  // Credit purchases now use the shared add-on modal flow for all tiers.
  const handleCreditsClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    openPlansModal('credits');
  };

  useEffect(() => {
    getTeams();
    // eslint-disable-next-line
  }, [teamName]);

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    fetchUserProfile(signal);
    return () => {
      controller.abort();
    };
    // eslint-disable-next-line
  }, [selectedTeam]);

  // Fetch current user's role in the selected team
  useEffect(() => {
    const fetchCurrentUserRole = async () => {
      if (!selectedTeam || !userData?.id) {
        setCurrentUserRole(null);
        setIsLoadingRole(false);
        return;
      }

      setIsLoadingRole(true);
      try {
        const membersResponse = await teamManagementService.getTeamMembers(selectedTeam.id, timezone);
        if (membersResponse.status === 200) {
          const members = membersResponse.data.results || [];
          const currentUserMember = members.find(member => member.user_id === userData.id);
          if (currentUserMember && currentUserMember.team_role) {
            setCurrentUserRole(currentUserMember.team_role.name);
          } else {
            setCurrentUserRole(null);
          }
        }
      } catch (error) {
        console.error('Error fetching current user role:', error);
        setCurrentUserRole(null);
      } finally {
        setIsLoadingRole(false);
      }
    };

    fetchCurrentUserRole();
    // eslint-disable-next-line
  }, [selectedTeam, userData?.id]);

  const fetchUserProfile = async (signal) => {
    try {
      const response = await userService.getUserProfile({ signal });
      if (response.status === 200) {
        setUser(response.data);
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Fetch aborted');
      } else {
        console.log('error', error);
      }
    }
  };

  const getTeams = async () => {
    const response = await teamManagementService.getTeams();
    setOwner(
      response.data.results.find((team) => team.owner_id === userData.id)
    );
    setTeams(response.data.results);
  };

  // Team management
  const handleTeamManagementClick = async () => {
    setTeamDropdownOpened(false);

    // Check if user has access to team management feature
    if (!featureCheck('team_management')) {
      return; // featureCheck will show the upgrade modal
    }

    try {
      if (
        teams.length === 0 ||
        !teams.some((team) => team.owner_id === userData?.id)
      ) {
        const response = await teamManagementService.createTeam(
          `${userData.full_name}'s team`
        );
        if (response.status === 200) {
          mixpanel.track('Team Created', {
            team_name: `${userData.full_name}'s team`,
          });
          setSelectedTeam(response.data);
          setUserMode(false);
          getTeams();
          fetchUserProfile();
          navigate(`/team-management/${response.data.id}`);
        }
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  const handleTeamClick = async (e, team) => {
    e.stopPropagation();
    e.preventDefault();

    mixpanel.track('Team Switched', {
      team_id: team.id,
      previous_team_id: selectedTeam?.id || null,
    });

    setUserMode(false);
    setSelectedTeam(team);
    setTeamName(team.name);
    resetSelectedChatbot();
    setTeamDropdownOpened(false);

    // Force navigation to home/chatbots list when switching teams
    window.location.href = '/';
  };

  // Handle opening the invite modal
  const handleOpenInviteModal = async (e, team) => {
    e.stopPropagation();

    if (!featureCheck('team_management')) return;

    setInviteButtonLoading(true);
    setSelectedTeamForInvite(team);

    try {
      // Fetch team roles
      const rolesResponse = await teamManagementService.getTeamRoles(team.id);
      if (rolesResponse.status === 200) {
        setRoles(rolesResponse.data.results || []);
      }

      // Fetch team members (includes nr_seats_from_addons, nr_seats_from_tier for seat check)
      const membersResponse = await teamManagementService.getTeamMembers(team.id, timezone);
      if (membersResponse.status === 200) {
        const membersData = membersResponse.data;
        const members = membersData.results || [];
        setTeamData(members);

        if (!checkCanAddTeamMember(membersData, members)) {
          return;
        }
      }

      setOpenAddMember(true);
    } catch (error) {
      console.error('Error fetching team data:', error);
      addErrorToast({ message: 'Failed to open invite modal', state: 'negative' });
    } finally {
      setInviteButtonLoading(false);
    }
  };

  // Handle submitting the invite
  const handleSubmitInvite = async () => {
    setLoading(true);
    setError({});

    try {
      if (!newMember.email) {
        setError({ email: 'Email is required' });
        setLoading(false);
        return;
      }

      if (!selectedTeamForInvite) {
        setError({ email: 'No team selected' });
        setLoading(false);
        return;
      }

      const response = await teamManagementService.createTeamMember(selectedTeamForInvite.id, newMember);

      if (response.status === 200) {
        addSuccessToast({ message: 'Invitation sent successfully', state: 'positive' });
        setOpenAddMember(false);

        // Reset form
        setNewMember({
          email: '',
          team_role_id: '',
          all_knowledge_bases: false,
          allowed_kbs: [],
          credits_available: 100,
        });

        // Refresh team data
        getTeams();
      }
    } catch (error) {
      console.error('Error inviting member:', error);
      addErrorToast({ message: 'Failed to send invitation', state: 'negative' });
    } finally {
      setLoading(false);
    }
  };

  // Get tier label for display
  const getTierLabel = (tierType) => {
    if (!tierType) return 'Free';
    const tier = tierType.toLowerCase();
    if (tier === 'free') return 'Free';
    if (tier === 'starter' || tier === 'entry') return 'Starter';
    if (tier === 'advanced' || tier === 'premium') return 'Advanced';
    if (tier === 'pro' || tier === 'professional' || tier === 'ultra') return 'Pro';
    if (tier === 'enterprise' || tier === 'business') return 'Enterprise';
    return tierType.charAt(0).toUpperCase() + tierType.slice(1);
  };

  // Calculate credits used percentage for progress bar (fills left to right as credits are used)
  const getCreditsUsedPercentage = (used, total) => {
    if (!total || total === 0) return 0;
    return Math.min(100, Math.max(0, (used / total) * 100));
  };

  // Format credits display for progress bar
  const formatCreditsDisplay = (used, total) => {
    if (total == null) return '0 / 0';
    const usedFormatted = formatCredits(used || 0);
    const totalFormatted = formatCredits(total || 0);
    return `${usedFormatted} / ${totalFormatted}`;
  };

  if (!userData?.id) {
    return null;
  }

  const isScheduledHibernation = userData?.tier_key?.scheduled_tier_type === 'hibernation';
  const isPausedAccount = userData?.tier_type === 'hibernation' && !userData?.will_hibernate && !isScheduledHibernation;

  // Render selected team item (with credits bar)
  const SelectedTeamItem = ({ team, isSelected = true }) => {
    // Check if current user is the owner of this team
    const isTeamOwner = team.owner_id === userData?.id;
    // Check if it's personal workspace
    const isPersonal = userMode || !selectedTeam;

    return (
      <div className={`flex flex-col justify-center  gap-2 bg-v3-black-lightest flex-1 ${isSelected ? 'rounded-v3-sm border border-v3-card-border h-[122px] md:h-[102px]' : 'border-b border-v3-card-border md:border-none'}  ${isTeamOwner || isPersonal ? 'px-v3-sm py-[12px] md:[8px]' : 'px-v3-sm py-[13px] h-[80px] md:h-[48px]'}`}>
        {/* Team header row */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {team.payment_status === 'payment_failed' && (
               <IconWrapper size={20}>
                <TriangleDangerIcon className="w-full h-full text-v3-orange-dark" />
              </IconWrapper>
            )}
            <span className="flex items-center text-v3-h9 font-medium text-black truncate max-w-[180px]">
              {isLoadingRole ? (
                <DSpinner style={{ height: 16, width: 16 }} />
              ) : (
                selectedTeam?.name || teamName
              )}
            </span>
          </div>
          {/* Show tier label and add user icon only if user is the owner */}
          {isTeamOwner && (
            <div className="flex items-center gap-v3-xs">
              <button
                onClick={handleTierLabelClick}
                className="text-v3-h10 rounded-v3-sm border border-v3-card-border px-v3-xs py-[7px] font-medium text-v3-purple-primary hover:text-v3-purple-medium transition-colors cursor-pointer"
              >
                {getTierLabel(userData.tier_type)}
              </button>
              <IconWrapper
                size={32}
                className={`p-v3-xxs ${inviteButtonLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                onClick={(e) => !inviteButtonLoading && handleOpenInviteModal(e, team)}
              >
                {inviteButtonLoading ? (
                  <DSpinner color="#7C3AED" style={{ height: 20, width: 20 }} />
                ) : (
                  <AddUserIcon className="size-5 text-v3-purple-primary hover:text-v3-purple-medium" />
                )}
              </IconWrapper>
            </div>
          )}
        </div>

        {/* Credits progress bar - show user's credits */}
        {(isTeamOwner || isPersonal) && (
          <div className="flex flex-col gap-[12px] pb-v3-xs">
            <div className="h-[4px] bg-white rounded-full overflow-hidden">
              <div
                className="h-full bg-v3-green-primary rounded-full transition-all duration-300"
                style={{
                  width: `${getCreditsUsedPercentage(userData?.credits_key?.credits_used, userData?.credits_key?.credits_available)}%`
                }}
              />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-v3-black-secondary text-v3-h9">Credits used</span>
              <span
                onClick={handleCreditsClick}
                className="text-v3-h9 font-medium text-v3-purple-primary hover:text-v3-purple-medium cursor-pointer"
              >
                {formatCreditsDisplay(userData?.credits_key?.credits_used, userData?.credits_key?.credits_available)}
              </span>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render unselected team item
  const UnselectedTeamItem = ({ team, onClick, showBorder = true, disabled = false }) => {
    // Check if current user is the owner of this team
    const isTeamOwner = team.owner_id === userData?.id;

    return (
      <button
        onClick={disabled ? undefined : onClick}
        disabled={disabled}
        className={`flex items-center justify-between w-full px-v3-sm py-[8px] md:h-[48px] h-[80px] transition-colors ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-v3-menu-hover'} ${showBorder ? 'border-b border-v3-card-border' : ''} md:border-none md:rounded-v3-lg`}
      >
        <div className="flex items-center gap-2">
          {team.payment_status === 'payment_failed' && (
            <IconWrapper size={20}>
              <TriangleDangerIcon className="w-full h-full text-v3-orange-dark" />
            </IconWrapper>
          )}
          <span className="text-sm font-regular text-v3-black-max truncate max-w-[180px] ">
            {team.name}
          </span>
        </div>
        {/* Show tier label only if user is the owner */}
        {isTeamOwner && (
          <span className="text-v3-h10 rounded-v3-sm border border-v3-card-border px-v3-xs py-[7px] font-medium text-v3-black-max">
            {getTierLabel(userData?.tier_type)}
          </span>
        )}
      </button>
    );
  };

  // Mobile button only (for main nav view) - renders just the button that triggers navigation
  if (isMobileView && isMobileButton) {
    const isPersonal = userMode || !selectedTeam;
    const Icon = isPersonal ? PersonIcon : UsersIcon;
    const tierType = isPersonal ? userData?.tier_type : selectedTeam?.tier_type;
    const paymentFailed = isPersonal
      ? userData?.payment_status === 'payment_failed'
      : selectedTeam?.payment_status === 'payment_failed';
    // Show tier label only if personal workspace OR user is owner of selected team
    const showTierLabel = isPersonal || (selectedTeam && selectedTeam.owner_id === userData?.id);
    const willPauseAccount = userData?.will_hibernate || userData?.tier_key?.scheduled_tier_type === 'hibernation';
    const isPausedAccountMobile = userData?.tier_type === 'paused';

    // Calculate days until pause date
    const calculateDaysUntilPause = () => {
      if (!userData?.tier_key?.renew_date) return null;
      const renewDate = new Date(userData.tier_key.renew_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      renewDate.setHours(0, 0, 0, 0);
      const diffTime = renewDate - today;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays > 0 ? diffDays : 0;
    };
    const daysUntilPause = willPauseAccount ? calculateDaysUntilPause() : null;

    return (
      <button
        onClick={onMobileButtonClick}
        className="flex items-center justify-between w-full text-v3-h10 py-[10px] font-regular rounded transition-colors hover:bg-v3-menu-hover cursor-pointer"
      >
        <div className="flex items-center gap-v3-xs">
          <IconWrapper size={20}>
            <Icon className="w-full h-full text-v3-disabled-text" />
          </IconWrapper>
          <span className="truncate max-w-[180px] text-v3-black-max font-medium tracking-tighter flex items-center">
            {isPersonal ? 'Personal Workspace' : (
              isLoadingRole ? (
                <DSpinner style={{ height: 16, width: 16 }} />
              ) : (
                selectedTeam?.name || teamName
              )
            )}
          </span>
          {/* Tier label - opens plans modal - only show if personal workspace or user is team owner */}
          {showTierLabel && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleTierLabelClick(e);
              }}
              className="text-v3-h10 font-medium text-v3-purple-primary hover:text-v3-purple-medium border border-v3-card-border rounded-v3-sm px-v3-xs py-[7px] transition-colors cursor-pointer"
            >
              {getTierLabel(tierType)}
            </button>
          )}
          {paymentFailed && !isPausedAccountMobile && !willPauseAccount && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                window.open('https://billing.stripe.com/p/login/9AQ3cQ88NaZ2f8k144', '_blank');
              }}
              className="border border-v3-card-border hover:border hover:border-v3-orange-primary rounded-v3-sm flex items-center px-[12px] py-v3-xxs gap-v3-xs hover:opacity-80 transition-opacity cursor-pointer"
            >
              <IconWrapper size={20}>
                <TriangleDangerIcon className="w-full h-full text-v3-orange-dark" />
              </IconWrapper>
            </button>
          )}
          {willPauseAccount && daysUntilPause !== null && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigate('/account-paused');
              }}
              className="border border-v3-card-border hover:border hover:border-v3-orange-primary rounded-v3-lg flex items-center px-[12px] py-v3-xxs gap-v3-xs hover:opacity-80 transition-opacity cursor-pointer"
            >
              <IconWrapper size={20}>
                <TriangleDangerIcon className="w-full h-full text-v3-orange-dark" />
              </IconWrapper>
            </button>
          )}
          {isPausedAccountMobile && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigate('/account-paused');
              }}
              className="border border-v3-card-border hover:border hover:border-v3-orange-primary rounded-v3-lg flex items-center px-[12px] py-v3-xxs gap-v3-xs hover:opacity-80 transition-opacity cursor-pointer"
            >
              <IconWrapper size={20}>
                <TriangleDangerIcon className="w-full h-full text-v3-orange-dark" />
              </IconWrapper>
            </button>
          )}
        </div>
        {/* Chevron - indicates clickable */}
        <IconWrapper size={20}>
          <RightChevronIcon className="w-full h-full text-v3-purple-primary" />
        </IconWrapper>
      </button>
    );
  }

  // Mobile full-page view
  if (isMobileView) {
    const handleMobileTeamClick = async (team) => {
      setUserMode(false);
      setSelectedTeam(team);
      setTeamName(team.name);
      resetSelectedChatbot();
      fetchUserProfile();

      // Force navigation to home/chatbots list when switching teams
      window.location.href = '/';

      onClose?.();
    };

    const handleMobilePersonalClick = () => {
      setSelectedTeam(null);
      setUserMode(true);
      resetSelectedChatbot();
      navigate('/');
      onClose?.();
    };

    const isPersonalSelected = userMode || !selectedTeam;

    return (
      <div className="md:hidden w-full h-full flex flex-col overflow-y-auto bg-white px-v3-sm sm:px-v3-lg">
          {/* Back Button */}
        <div className="h-[40px] my-[12px]">
          <button onClick={onBack} className="flex items-center gap-v3-xs text-v3-purple-primary  py-[10px]">
            <IconWrapper size={20}>
              <LeftArrowIcon className="w-full h-full text-v3-purple-primary" />
            </IconWrapper>
            <span className="text-v3-h10 font-medium">Back</span>
          </button>
        </div>

        {/* Team List */}
        <div className="flex-1 flex flex-col">
          {/* Personal Workspace */}
          {isPersonalSelected ? (
            <div className="flex flex-col gap-2 bg-v3-black-lightest border border-v3-card-border rounded-v3-sm px-v3-sm py-[12px] md:py-[8px]">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {userData?.payment_status === 'payment_failed' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open('https://billing.stripe.com/p/login/9AQ3cQ88NaZ2f8k144', '_blank');
                      }}
                      className="cursor-pointer h-[20px] w-[20px]"
                    >
                      <IconWrapper size={20}>
                        <TriangleDangerIcon className="w-full h-full text-v3-orange-dark" />
                      </IconWrapper>
                    </button>
                  )}
                  <span className="text-sm font-medium text-black">Personal Workspace</span>
                </div>
                <span className={`text-v3-h10 rounded-v3-sm border border-v3-card-border px-v3-xs py-[7px] font-medium ${isPersonalSelected ? 'text-v3-purple-primary' : 'text-v3-black-max'}`}>
                  {getTierLabel(userData?.tier_type)}
                </span>
              </div>
              {/* Credits progress bar - show user's credits */}
              <div className="flex flex-col gap-[12px] pb-v3-xs">
                <div className="h-[4px] bg-white rounded-full overflow-hidden">
                  <div
                    className="h-full bg-v3-green-primary rounded-full transition-all duration-300"
                    style={{
                      width: `${getCreditsUsedPercentage(userData?.credits_key?.credits_used, userData?.credits_key?.credits_available)}%`
                    }}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-v3-black-secondary text-v3-h9">Credits used</span>
                  <span
                    onClick={handleCreditsClick}
                    className="text-v3-h9 font-medium text-v3-purple-primary hover:text-v3-purple-medium cursor-pointer"
                  >
                    {formatCreditsDisplay(userData?.credits_key?.credits_used, userData?.credits_key?.credits_available)}
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <button
              onClick={handleMobilePersonalClick}
              className={`flex items-center justify-between w-full py-v3-md px-v3-sm hover:bg-v3-menu-hover transition-colors ${teams.length > 0 && !userMode && selectedTeam && teams[0].id === selectedTeam.id ? '' : 'border-b border-v3-card-border'}`}
            >
              <span className="text-sm font-regular text-v3-black-max">Personal Workspace</span>
              <span className="text-v3-h10 rounded-v3-sm border border-v3-card-border px-v3-xs py-[7px] font-medium text-v3-purple-primary">
                {getTierLabel(userData?.tier_type)}
              </span>
            </button>
          )}

          {/* Teams */}
          {teams.map((team, index) => {
            const isTeamSelected = !userMode && selectedTeam && team.id === selectedTeam.id;
            const nextTeam = teams[index + 1];
            const isNextTeamSelected = !userMode && selectedTeam && nextTeam && nextTeam.id === selectedTeam.id;
            const isTeamDisabled = isPausedAccount && team.owner_id === userData?.id;

            if (isTeamSelected) {
              return (
                <div key={team.id}>
                  <SelectedTeamItem team={team} isSelected={true} />
                </div>
              );
            }

            return (
              <div key={team.id}>
                <UnselectedTeamItem
                  team={team}
                  onClick={() => handleMobileTeamClick(team)}
                  showBorder={!isNextTeamSelected}
                  disabled={isTeamDisabled}
                />
              </div>
            );
          })}

          {/* Create New Team - only show if user doesn't own a team */}
          {(teams.length === 0 ||
            !teams.some(
              (team) => team.owner_id === userData?.id
            )) && (
            <div className="py-v3-md text-center">
              <button
                onClick={handleTeamManagementClick}
                className="text-sm font-medium text-v3-purple-primary hover:opacity-80 transition-opacity"
              >
                + Create New Team
              </button>
            </div>
          )}
        </div>

        {/* AddMember Modal */}
        <AddMember
          open={openAddMember}
          onClose={() => {
            setOpenAddMember(false);
            setNewMember({
              email: '',
              team_role_id: '',
              all_knowledge_bases: false,
              allowed_kbs: [],
              credits_available: 100,
            });
            setError({});
          }}
          onSubmit={handleSubmitInvite}
          newMember={newMember}
          setNewMember={setNewMember}
          roles={roles}
          loading={loading}
          error={error}
          teamData={teamData}
            teamOwner={selectedTeamForInvite?.owner_id}
            teamDashboardId={teamData?.id}
          teams={teams}
          setOpenEditRole={() => {}}
          setOpenDeleteRole={() => {}}
          setSelectedRole={() => {}}
          setOpenAddRole={() => {}}
        />
      </div>
    );
  }

  return (
    <Menu>
      {({ open }) => {
        if (teamDropdownOpened !== open) {
          setTeamDropdownOpened(open);
        }
        return (
          <>
            <div className={`flex items-center gap-v3-xxs text-v3-h10 lg:pl-v3-sm font-regular rounded transition-colors ${isMobileView ? 'w-full justify-between' : ''}`}>
              <div className="flex items-center gap-v3-xs">
                {(() => {
                  const isPersonal = userMode || !selectedTeam;
                  const Icon = isPersonal ? PersonIcon : UsersIcon;
                  const paymentFailed = isPersonal
                    ? userData?.payment_status === 'payment_failed'
                    : selectedTeam?.payment_status === 'payment_failed';
                  // Show tier label only if personal workspace OR user is owner of selected team
                  const showTierLabel = isPersonal || (selectedTeam && selectedTeam.owner_id === userData?.id);
                  const isPausedAccount = userData?.tier_type === 'paused';
                  const willPauseAccount = userData?.will_hibernate || userData?.tier_key?.scheduled_tier_type === 'hibernation';
                  const isDemoAccount = userData?.is_demo_account;
                  // Calculate days until pause date
                  const calculateDaysUntilPause = () => {
                    if (!userData?.tier_key?.renew_date) return null;
                    const renewDate = new Date(userData.tier_key.renew_date);
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    renewDate.setHours(0, 0, 0, 0);
                    const diffTime = renewDate - today;
                    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                    return diffDays > 0 ? diffDays : 0;
                  };
                  const daysUntilPause = willPauseAccount ? calculateDaysUntilPause() : null;

                  return (
                    <>
                      <IconWrapper size={20}>
                        <Icon className="w-full h-full text-v3-disabled-text" />
                      </IconWrapper>
                      <span className="truncate max-w-[220px] text-v3-black-max font-medium tracking-tighter flex items-center">
                        {isPersonal ? 'Personal Workspace' : (
                          isLoadingRole ? (
                            <DSpinner style={{ height: 16, width: 16 }} />
                          ) : (
                            selectedTeam?.name || teamName
                          )
                        )}
                      </span>
                      {/* Tier label - opens plans modal - only show if personal workspace or user is team owner */}
                      {showTierLabel && (
                        <button
                          onClick={handleTierLabelClick}
                          className="text-v3-h10 font-medium text-v3-purple-primary border border-v3-card-border rounded-v3-sm px-v3-xs py-[7px] hover:text-v3-purple-medium transition-colors cursor-pointer"
                        >
                          {getTierLabel(userData?.tier_type)}
                        </button>
                      )}
                      {paymentFailed && !isPausedAccount && !willPauseAccount && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open('https://billing.stripe.com/p/login/9AQ3cQ88NaZ2f8k144', '_blank');
                          }}
                          className="group border border-v3-card-border hover:border-v3-orange-primary rounded-v3-sm flex items-center px-[12px] py-v3-xxs gap-v3-xs transition-all cursor-pointer"
                        >
                          <IconWrapper size={20}>
                            <TriangleDangerIcon className="w-full h-full text-v3-orange-dark group-hover:text-v3-orange-primary transition-colors" />
                          </IconWrapper>
                          <span className="text-v3-h10 font-medium text-v3-orange-dark group-hover:text-v3-orange-primary transition-colors">
                            Payment Failed
                          </span>
                        </button>
                      )}
                      {isDemoAccount && (
                        <div
                          className="group border min-h-8 border-v3-card-border rounded-v3-sm flex items-center px-[12px] py-v3-xxs gap-v3-xs transition-all"
                        >
                          <span className="text-v3-h10 font-medium text-v3-purple-primary transition-colors">
                            This is a demo account
                          </span>
                        </div>
                      )}
                      
                      {willPauseAccount && daysUntilPause !== null && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate('/account-paused');
                          }}
                          className="group border border-v3-card-border hover:border-v3-orange-primary rounded-v3-lg flex items-center px-[12px] py-v3-xxs gap-v3-xs transition-all cursor-pointer"
                        >
                          <IconWrapper size={20}>
                            <TriangleDangerIcon className="w-full h-full text-v3-orange-dark group-hover:text-v3-orange-primary transition-colors" />
                          </IconWrapper>
                          <span className="text-v3-h10 font-medium text-v3-orange-dark group-hover:text-v3-orange-primary transition-colors">
                            Pausing in {daysUntilPause} {daysUntilPause === 1 ? 'day' : 'days'}
                          </span>
                        </button>
                      )}
                      {isPausedAccount && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate('/account-paused');
                          }}
                          className="group border border-v3-card-border hover:border-v3-orange-primary rounded-v3-lg flex items-center px-[12px] py-v3-xxs gap-v3-xs transition-all cursor-pointer"
                        >
                          <IconWrapper size={20}>
                            <TriangleDangerIcon className="w-full h-full text-v3-orange-dark group-hover:text-v3-orange-primary transition-colors" />
                          </IconWrapper>
                          <span className="text-v3-h10 font-medium text-v3-orange-dark group-hover:text-v3-orange-primary transition-colors">
                            Subscription Paused
                          </span>
                        </button>
                      )}
                    </>
                  );
                })()}
              </div>
              {/* Chevron - opens dropdown menu */}
              <MenuButton className=" group p-1 rounded-v3-sm flex items-center justify-center transition-colors cursor-pointer">
                <IconWrapper size={20}>
                  {isMobileView ? (
                    <RightChevronIcon className="w-full h-full text-v3-purple-primary group-hover:text-v3-purple-medium transition-colors" />
                  ) : (
                    <ChevronDownIcon className={`w-full h-full text-v3-purple-primary hover:text-v3-purple-medium transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
                  )}
                </IconWrapper>
              </MenuButton>
            </div>

            <MenuItems
              anchor="bottom end"
              className="w-[400px] origin-top-left rounded-v3-sm border border-v3-card-border shadow-v3-card bg-white text-black transition duration-100 ease-out [--anchor-gap:var(--spacing-1)] focus:outline-none data-[closed]:scale-95 data-[closed]:opacity-0 z-50 flex flex-col overflow-hidden mt-[14px] ml-v3-xhuge "
            >
              {/* Personal Workspace - Always at top */}
              {userMode || !selectedTeam ? (
                // Personal Workspace selected - show as header
                <div className="flex flex-col gap-2 px-v3-sm py-[12px] md:py-[8px] bg-v3-black-lightest rounded-v3-sm border border-v3-card-border">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {userData?.payment_status === 'payment_failed' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open('https://billing.stripe.com/p/login/9AQ3cQ88NaZ2f8k144', '_blank');
                          }}
                          className="cursor-pointer h-[20px] w-[20px]"
                        >
                          <IconWrapper size={20}>
                            <TriangleDangerIcon className="w-full h-full text-v3-orange-dark" />
                          </IconWrapper>
                        </button>
                      )}
                      <span className="text-sm font-medium text-v3-black-max">
                        Personal Workspace
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={handleTierLabelClick}
                        className={`cursor-pointer  hover:text-v3-purple-medium text-v3-h10 rounded-v3-sm px-v3-xs py-[7px] border border-v3-card-border  font-medium ${userMode && selectedTeam === null ? 'text-v3-purple-primary' : 'text-v3-black-max'}`}>
                        {getTierLabel(userData?.tier_type)}
                      </button>
                    </div>
                  </div>
                  {/* Credits progress bar - show user's credits */}
                  <div className="flex flex-col gap-[12px] pb-v3-xs">
                    <div className="h-[4px] bg-white rounded-full overflow-hidden">
                      <div
                        className="h-full bg-v3-green-primary rounded-full transition-all duration-300"
                        style={{
                          width: `${getCreditsUsedPercentage(userData?.credits_key?.credits_used, userData?.credits_key?.credits_available)}%`
                        }}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-v3-black-secondary text-v3-h9">Credits used</span>
                      <span
                        onClick={handleCreditsClick}
                        className="text-v3-h9 font-medium text-v3-purple-primary hover:text-v3-purple-medium cursor-pointer"
                      >
                        {formatCreditsDisplay(userData?.credits_key?.credits_used, userData?.credits_key?.credits_available)}
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                // Personal Workspace as clickable option when team is selected
                <MenuItem>
                  {({ focus }) => (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedTeam(null);
                        setUserMode(true);
                        setTeamDropdownOpened(false);
                        resetSelectedChatbot();
                        // Force navigation to home/chatbots list when switching to personal workspace
                        window.location.href = '/';
                      }}
                      className={`flex items-center justify-between w-full px-v3-sm py-[8px] ${teams.length > 0 && !userMode && selectedTeam && teams[0].id === selectedTeam.id ? '' : 'border-b border-v3-card-border md:border-none md:rounded-v3-sm'} ${focus ? 'bg-v3-menu-hover' : ''} transition-colors`}
                    >
                      <span className="text-sm font-regular text-v3-black-max">
                        Personal Workspace
                      </span>
                      <div className="flex items-center gap-2">
                        <span className={`text-v3-h10 rounded-v3-sm px-v3-xs py-[7px] border border-v3-card-border font-medium ${userMode && selectedTeam === null ? 'text-v3-purple-primary' : 'text-v3-black-max'}`}>
                          {getTierLabel(userData?.tier_type)}
                        </span>
                      </div>
                    </button>
                  )}
                </MenuItem>
              )}

              {/* Teams list - maintain original order, show selected team with special styling */}
              <div className="flex flex-col">
                {teams.map((team, index) => {
                  const nextTeam = teams[index + 1];
                  const isNextTeamSelected = !userMode && selectedTeam && nextTeam && nextTeam.id === selectedTeam.id;
                  const isTeamDisabled = isPausedAccount && team.owner_id === userData?.id;

                  return (
                    <div key={team.id}>
                      {!userMode && selectedTeam && team.id === selectedTeam.id ? (
                        <SelectedTeamItem team={team} isSelected={true} />
                      ) : (
                        <MenuItem className="rounded-v3-sm">
                          {({ focus }) => (
                            <div className={focus ? 'bg-v3-menu-hover rounded-v3-sm' : ''}>
                              <UnselectedTeamItem
                                team={team}
                                onClick={(e) => handleTeamClick(e, team)}
                                showBorder={!isNextTeamSelected}
                                disabled={isTeamDisabled}
                              />
                            </div>
                          )}
                        </MenuItem>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Create New Team button - only show if user doesn't own a team */}
              {(teams.length === 0 ||
                !teams.some(
                  (team) => team.owner_id === userData?.id
                )) && (
                <div className="px-v3-sm py-v3-sm text-center">
                  <button
                    onClick={handleTeamManagementClick}
                    className="text-v3-h10 font-medium  py-[10px] text-v3-purple-primary hover:opacity-80 transition-opacity"
                  >
                    + Create New Team
                  </button>
                </div>
              )}
            </MenuItems>

            {/* AddMember Modal */}
            <AddMember
              open={openAddMember}
              onClose={() => {
                setOpenAddMember(false);
                setNewMember({
                  email: '',
                  team_role_id: '',
                  all_knowledge_bases: false,
                  allowed_kbs: [],
                  credits_available: 100,
                });
                setError({});
              }}
              onSubmit={handleSubmitInvite}
              newMember={newMember}
              setNewMember={setNewMember}
              roles={roles}
              loading={loading}
              error={error}
              teamData={teamData}
              teamOwner={selectedTeamForInvite?.owner_id}
              teams={teams}
              setOpenEditRole={() => {}}
              setOpenDeleteRole={() => {}}
              setSelectedRole={() => {}}
              setOpenAddRole={() => {}}
            />
          </>
        );
      }}
    </Menu>
  );
};

export default TeamManagementDropdown;
